import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Loader2, Search, X } from 'lucide-react';
import { locationAPI } from '../api';

/* One location definition for the whole CRM.
   Country -> State/Region -> District/County (when the country has it) -> City
   -> Locality (controlled text) . Address stays free text in the parent form. */

export const emptyLocation = {
  country_code: '', country: '', state_code: '', state: '',
  district_code: '', district: '', city: '', locality: '',
};

export const locationLabel = (l = {}) => {
  const parts = [l.locality, l.city, l.district !== l.city ? l.district : null, l.state, l.country];
  const seen = new Set();
  return parts
    .filter((p) => p && !seen.has(String(p).toLowerCase()) && seen.add(String(p).toLowerCase()))
    .join(', ');
};

const fld = 'w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-primary/40 outline-none';

/* Searchable single-select. Options are fetched from the server on open/typing, so the
   browser never downloads the worldwide dataset. */
const Combo = ({ value, display, placeholder, disabled, fetcher, onPick, onClear, allowFree,
  testId, emptyHint }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const box = useRef(null);
  const timer = useRef(null);

  const run = useCallback(async (term) => {
    if (!fetcher) return;
    setLoading(true);
    try { setItems(await fetcher(term)); } catch { setItems([]); } finally { setLoading(false); }
  }, [fetcher]);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => run(q), q ? 220 : 0);
    return () => timer.current && clearTimeout(timer.current);
  }, [open, q, run]);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  return (
    <div className="relative" ref={box}>
      <button type="button" data-testid={testId} disabled={disabled}
        onClick={() => { setOpen((o) => !o); setQ(''); }}
        className={`${fld} flex items-center justify-between gap-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${open ? 'ring-2 ring-primary/40' : ''}`}>
        <span className={`truncate ${display ? '' : 'text-slate-400'}`}>{display || placeholder}</span>
        <span className="flex shrink-0 items-center gap-1">
          {display && !disabled && (
            <X data-testid={testId ? `${testId}-clear` : undefined} className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700"
              onClick={(e) => { e.stopPropagation(); onClear?.(); setOpen(false); }} />
          )}
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div data-testid={testId ? `${testId}-menu` : undefined}
          className="absolute z-40 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center gap-2 border-b border-slate-100 px-2.5 py-2 dark:border-slate-800">
            <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              data-testid={testId ? `${testId}-search` : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && allowFree && q.trim()) { e.preventDefault(); onPick({ name: q.trim() }); setOpen(false); }
                if (e.key === 'Escape') setOpen(false);
              }}
              placeholder="Type to search…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
            {loading && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" />}
          </div>
          <div className="max-h-56 overflow-y-auto overscroll-contain py-1">
            {items.map((it) => (
              <button key={it.code || it.name} type="button"
                data-testid={testId ? `${testId}-opt-${(it.name || '').replace(/\s+/g, '-').toLowerCase()}` : undefined}
                onClick={() => { onPick(it); setOpen(false); }}
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                <span className="truncate">{it.name}</span>
                {(value && (value === it.code || value === it.name)) && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
              </button>
            ))}
            {!loading && !items.length && (
              <p className="px-3 py-2 text-xs text-slate-400">
                {allowFree && q.trim() ? `Press Enter to use “${q.trim()}”` : (emptyHint || 'No matches')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const useLabels = (countryCode) => {
  const [labels, setLabels] = useState(null);
  useEffect(() => {
    let live = true;
    if (!countryCode) { setLabels(null); return undefined; }
    locationAPI.states(countryCode).then(({ data }) => live && setLabels(data.labels)).catch(() => {});
    return () => { live = false; };
  }, [countryCode]);
  return labels || { state: 'State / Region', district: 'District', city: 'City' };
};

/* value: subset of emptyLocation . onChange(patch) is called with every cleared child level,
   so an incompatible child can never stay selected after a parent change. */
export const LocationPicker = ({ value = {}, onChange, disabled, compact = false,
  showLocality = true, showDistrict = true, idPrefix = 'loc' }) => {
  const v = { ...emptyLocation, ...value };
  const labels = useLabels(v.country_code);
  const [hasDistricts, setHasDistricts] = useState(false);

  useEffect(() => {
    let live = true;
    if (!v.country_code || !v.state_code) { setHasDistricts(false); return undefined; }
    locationAPI.districts(v.country_code, v.state_code)
      .then(({ data }) => live && setHasDistricts((data.items || []).length > 0))
      .catch(() => live && setHasDistricts(false));
    return () => { live = false; };
  }, [v.country_code, v.state_code]);

  const countries = useCallback(async (q) => (await locationAPI.countries(q)).data.items, []);
  const states = useCallback(async (q) => (await locationAPI.states(v.country_code, q)).data.items, [v.country_code]);
  const districts = useCallback(async (q) => (await locationAPI.districts(v.country_code, v.state_code, q)).data.items, [v.country_code, v.state_code]);
  const cities = useCallback(async (q) => (await locationAPI.cities(v.country_code, v.state_code, v.district_code, q)).data.items
    .map((c) => ({ name: c.name, code: c.name, district_code: c.district_code, district: c.district })), [v.country_code, v.state_code, v.district_code]);
  const localities = useCallback(async (q) => (await locationAPI.localities(v.city, v.country_code, q)).data.items
    .map((n) => ({ name: n, code: n })), [v.city, v.country_code]);

  const cell = compact ? '' : 'min-w-0';
  const Label = ({ children }) => (compact ? null : <label className="mb-1 block text-xs font-medium">{children}</label>);

  return (
    <>
      <div className={cell}>
        <Label>Country</Label>
        <Combo testId={`${idPrefix}-country`} value={v.country_code} display={v.country} placeholder="Select country"
          disabled={disabled} fetcher={countries}
          onPick={(it) => onChange({ country_code: it.code, country: it.name, state_code: '', state: '', district_code: '', district: '', city: '', locality: '' })}
          onClear={() => onChange({ ...emptyLocation })} />
      </div>

      <div className={cell}>
        <Label>{labels.state}</Label>
        <Combo testId={`${idPrefix}-state`} value={v.state_code} display={v.state}
          placeholder={v.country_code ? `Select ${labels.state.toLowerCase()}` : 'Select country first'}
          disabled={disabled || !v.country_code} fetcher={states}
          onPick={(it) => onChange({ state_code: it.code, state: it.name, district_code: '', district: '', city: '', locality: '' })}
          onClear={() => onChange({ state_code: '', state: '', district_code: '', district: '', city: '', locality: '' })} />
      </div>

      {showDistrict && hasDistricts && (
        <div className={cell}>
          <Label>{labels.district}</Label>
          <Combo testId={`${idPrefix}-district`} value={v.district_code} display={v.district}
            placeholder={`Select ${labels.district.toLowerCase()} (optional)`}
            disabled={disabled || !v.state_code} fetcher={districts}
            onPick={(it) => onChange({ district_code: it.code, district: it.name, city: '', locality: '' })}
            onClear={() => onChange({ district_code: '', district: '', city: '', locality: '' })} />
        </div>
      )}

      <div className={cell}>
        <Label>{labels.city}</Label>
        <Combo testId={`${idPrefix}-city`} value={v.city} display={v.city}
          placeholder={v.state_code ? `Select ${labels.city.toLowerCase()}` : `Select ${labels.state.toLowerCase()} first`}
          disabled={disabled || !v.state_code} fetcher={cities}
          emptyHint="No matching city in this area"
          onPick={(it) => onChange({ city: it.name, locality: '',
            ...(it.district_code ? { district_code: it.district_code, district: it.district || v.district } : {}) })}
          onClear={() => onChange({ city: '', locality: '' })} />
      </div>

      {showLocality && (
        <div className={cell}>
          <Label>Locality / Area</Label>
          <Combo testId={`${idPrefix}-locality`} value={v.locality} display={v.locality}
            placeholder={v.city ? 'Select or type locality' : 'Select city first'}
            disabled={disabled || !v.city} fetcher={localities} allowFree
            emptyHint="Type a locality and press Enter"
            onPick={(it) => onChange({ locality: it.name })}
            onClear={() => onChange({ locality: '' })} />
        </div>
      )}
    </>
  );
};

/* Compact country/state/city trio for filter bars and analytics drill-downs. */
export const LocationFilter = ({ value = {}, onChange, idPrefix = 'locf', showDistrict = false }) => (
  <LocationPicker value={{ ...emptyLocation, ...value }} onChange={onChange} compact
    showLocality={false} showDistrict={showDistrict} idPrefix={idPrefix} />
);

export const useLocationLabels = useLabels;
export default LocationPicker;
